import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, XCircle, Loader2, Users, FileText, ClipboardList, UserPlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatCurrency, formatDate } from '@/lib/api';

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState({ receivable: [], payable: [], purchase_orders: [] });
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', password: '' });
  const [inviting, setInviting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [u, a] = await Promise.all([api.get('/admin/users'), api.get('/admin/activity')]);
      setUsers(u.data);
      setActivity(a.data);
    } catch { toast.error('Failed to load admin data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const approveUser = async (id) => {
    try { await api.patch(`/admin/users/${id}/approve`); toast.success('User approved'); fetchData(); }
    catch { toast.error('Failed to approve'); }
  };

  const rejectUser = async (id) => {
    try { await api.patch(`/admin/users/${id}/reject`); toast.success('User rejected'); fetchData(); }
    catch { toast.error('Failed to reject'); }
  };

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.name || !inviteForm.password) { toast.error('All fields are required'); return; }
    setInviting(true);
    try {
      await api.post('/admin/invite-user', inviteForm);
      toast.success(`User ${inviteForm.email} invited successfully`);
      setInviteOpen(false);
      setInviteForm({ name: '', email: '', password: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to invite user'); }
    setInviting(false);
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success('User deleted'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const deleteInvoice = async (type, id, number) => {
    if (!window.confirm(`Delete ${number}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/invoices/${type}/${id}`); toast.success(`${number} deleted`); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const deletePo = async (id, number) => {
    if (!window.confirm(`Delete ${number}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/purchase-orders/${id}`); toast.success(`${number} deleted`); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const pendingUsers = users.filter(u => u.status === 'pending_approval');

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" size={24} /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Panel</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage users and view all activity</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="invite-user-button">
          <UserPlus size={16} /> Invite User
        </Button>
      </div>

      {pendingUsers.length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-amber-400 flex items-center gap-2"><Users size={16} /> Pending Approvals ({pendingUsers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingUsers.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3 border border-border rounded-md bg-card" data-testid={`pending-user-${u.id}`}>
                  <div>
                    <p className="text-foreground font-medium">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.email} &middot; Registered {formatDate(u.created_at)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approveUser(u.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-3 text-xs gap-1" data-testid={`approve-user-${u.id}`}>
                      <CheckCircle size={12} /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => rejectUser(u.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-7 px-3 text-xs gap-1" data-testid={`reject-user-${u.id}`}>
                      <XCircle size={12} /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="users" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white gap-1"><Users size={14} /> All Users</TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white gap-1"><FileText size={14} /> Invoices</TabsTrigger>
          <TabsTrigger value="pos" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white gap-1"><ClipboardList size={14} /> Purchase Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Name</TableHead>
                    <TableHead className="text-muted-foreground">Email</TableHead>
                    <TableHead className="text-muted-foreground">Role</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Registered</TableHead>
                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} className="border-border hover:bg-muted/50" data-testid={`user-row-${u.id}`}>
                      <TableCell className="text-foreground font-medium">{u.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs capitalize">{u.role}</Badge></TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs capitalize ${u.status === 'approved' ? 'text-emerald-400 border-emerald-400/30' : u.status === 'pending_approval' ? 'text-amber-400 border-amber-400/30' : 'text-red-400 border-red-400/30'}`}>
                          {u.status === 'pending_approval' ? 'Pending' : u.status || 'Active'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(u.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {u.status === 'pending_approval' && (
                            <>
                              <Button size="sm" variant="ghost" className="text-emerald-400 h-7 px-2" onClick={() => approveUser(u.id)} title="Approve"><CheckCircle size={14} /></Button>
                              <Button size="sm" variant="ghost" className="text-red-400 h-7 px-2" onClick={() => rejectUser(u.id)} title="Reject"><XCircle size={14} /></Button>
                            </>
                          )}
                          {u.status === 'rejected' && (
                            <Button size="sm" variant="ghost" className="text-emerald-400 h-7 px-2" onClick={() => approveUser(u.id)} title="Re-activate"><CheckCircle size={14} /></Button>
                          )}
                          {u.role !== 'admin' && (
                            <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-400 h-7 px-2" onClick={() => deleteUser(u.id, u.email)} title="Delete User" data-testid={`delete-user-${u.id}`}><Trash2 size={14} /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-base text-card-foreground">All Invoices (AR + AP)</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Type</TableHead>
                    <TableHead className="text-muted-foreground">Invoice #</TableHead>
                    <TableHead className="text-muted-foreground">Party</TableHead>
                    <TableHead className="text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Created By</TableHead>
                    <TableHead className="text-muted-foreground">Date</TableHead>
                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...activity.receivable.map(i => ({ ...i, _type: 'AR' })), ...activity.payable.map(i => ({ ...i, _type: 'AP' }))]
                    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                    .slice(0, 50)
                    .map(inv => (
                    <TableRow key={inv.id} className="border-border hover:bg-muted/50">
                      <TableCell><Badge variant="outline" className={`text-xs ${inv._type === 'AR' ? 'text-blue-400 border-blue-400/30' : 'text-violet-400 border-violet-400/30'}`}>{inv._type}</Badge></TableCell>
                      <TableCell className="text-card-foreground/80 font-mono text-xs">{inv.invoice_number}</TableCell>
                      <TableCell className="text-foreground text-sm">{inv.client_name || inv.vendor_name}</TableCell>
                      <TableCell className="text-foreground font-mono text-sm">{formatCurrency(inv.amount, inv.currency)}</TableCell>
                      <TableCell><Badge variant="outline" className={`status-${inv.status} text-xs capitalize`}>{inv.status}</Badge></TableCell>
                      <TableCell className="text-muted-foreground text-xs">{inv.created_by || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(inv.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-400 h-7 px-2"
                          onClick={() => deleteInvoice(inv._type === 'AR' ? 'receivable' : 'payable', inv.id, inv.invoice_number)}
                          title="Delete" data-testid={`admin-delete-inv-${inv.id}`}><Trash2 size={14} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pos">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-base text-card-foreground">All Purchase Orders</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">PO #</TableHead>
                    <TableHead className="text-muted-foreground">Vendor</TableHead>
                    <TableHead className="text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Created By</TableHead>
                    <TableHead className="text-muted-foreground">Date</TableHead>
                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activity.purchase_orders.slice(0, 50).map(po => (
                    <TableRow key={po.id} className="border-border hover:bg-muted/50">
                      <TableCell className="text-card-foreground/80 font-mono text-xs">{po.po_number}</TableCell>
                      <TableCell className="text-foreground text-sm">{po.vendor_name}</TableCell>
                      <TableCell className="text-foreground font-mono text-sm">{formatCurrency(po.amount, po.currency)}</TableCell>
                      <TableCell><Badge variant="outline" className={`status-${po.status} text-xs capitalize`}>{po.status}</Badge></TableCell>
                      <TableCell className="text-muted-foreground text-xs">{po.created_by || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(po.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-400 h-7 px-2"
                          onClick={() => deletePo(po.id, po.po_number)}
                          title="Delete" data-testid={`admin-delete-po-${po.id}`}><Trash2 size={14} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite User Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="invite-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-card-foreground flex items-center gap-2"><UserPlus size={18} className="text-blue-400" /> Invite New User</DialogTitle>
            <DialogDescription className="text-muted-foreground">Create an account for a new user. They will receive login credentials via email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-card-foreground/80 text-sm">Full Name *</Label>
              <Input value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="invite-name-input" placeholder="John Doe" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-card-foreground/80 text-sm">Email *</Label>
              <Input type="email" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="invite-email-input" placeholder="user@company.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-card-foreground/80 text-sm">Temporary Password *</Label>
              <Input value={inviteForm.password} onChange={e => setInviteForm({ ...inviteForm, password: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="invite-password-input" placeholder="Set initial password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} className="border-input text-card-foreground/80">Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="confirm-invite-button">
              {inviting ? <Loader2 className="animate-spin" size={16} /> : <UserPlus size={16} />} Invite User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
